import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const CaudaEquinaCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
    title: "Cauda Equina Syndrome in New York City",
    metaDescription: "Urgent care for Cauda Equina Syndrome in New York. Learn about symptoms, emergency treatment, and recovery at NY Center for Spinal Disorders.",
    metaKeywords: "Cauda Equina Syndrome, emergency spine care NYC, Dr. Shah, Dr. Tindel, spinal nerve compression",
    heroDescription: "Expert evaluation and treatment for Cauda Equina Syndrome with a focus on urgent care and effective recovery strategies.",
    forWho: [
      "Cauda Equina Syndrome is a rare but critical condition in which the bundle of nerve roots at the very bottom of the spinal cord (the cauda equina) becomes severely compressed. This compression can result from a massive disc herniation in the lower back, spinal stenosis, trauma, or a tumor. Because these nerves control functions in the pelvic region—such as bladder, bowel, and lower limb sensation—compression can lead to serious complications.",
      "Common warning signs include severe lower back pain, sudden loss of bladder or bowel control, numbness or tingling in the “saddle” area (buttocks, inner thighs), and weakness or numbness in the legs. This condition is considered a surgical emergency because delaying treatment increases the risk of permanent nerve damage."
    ],
    conditionInfo: [
      "If you notice any combination of these alarming symptoms, seek immediate medical attention. The specialist team at the NY Center for Spinal Disorders in Manhattan can quickly evaluate your condition and, if necessary, perform urgent surgery to relieve pressure on the cauda equina.",
    ],
    treatment: [
      "Emergency decompression surgery is often required to relieve pressure on the affected nerves.",
      "Once stabilized, rehabilitation focuses on restoring muscle strength and nerve function.",
      "Many patients regain substantial function, though recovery times can vary."
    ],
    postCare: [
      "Follow-up visits track nerve healing and address any lingering issues like numbness or weakness.",
      "Our team offers physical therapy and lifestyle guidance to support a smooth recovery journey."
    ],
    nonSurgicalOptions: [
      "In rare, mild presentations, conservative measures may be used until surgery is scheduled.",
      "Pain management and limited activity could be recommended short-term."
    ],
    faqs: [
      {
        id: 1,
        question: "Is Cauda Equina Syndrome life-threatening?",
        answer: "While not usually fatal, it’s a surgical emergency. Delayed treatment can lead to permanent deficits."
      },
      {
        id: 2,
        question: "Will I recover full nerve function?",
        answer: "Early intervention improves the chances, but outcomes differ among patients."
      }
    ]
  };  

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
         <title>{pageData.title} - NYC for Spinal Disorders</title>
          <meta name="description" content={pageData.metaDescription} />
          <meta name="keywords" content={pageData.metaKeywords} />
          <meta name="author" content="New York Center for Spinal Disorders" />
          <meta property="og:title" content={pageData.title} />
          <meta property="og:description" content={pageData.metaDescription} />
          <meta property="og:image" content="/images/logo-header.png" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      
      <Header />
      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt={pageData.title}
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>



        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CaudaEquinaCondition;